import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EChartsOption } from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { HeatmapDataItem } from 'src/app/company/company.model';
import { EchartsConfigService } from 'src/shared/services/echarts-config/echarts-config.service';

interface GradientRange {
  type: 'Positive' | 'Negative';
  level: number;
  min: number;
  max: number;
  color: string;
}

@Component({
  selector: 'mohlel-heatmap-chart',
  imports: [NgxEchartsModule, EmptyStateComponent],
  templateUrl: './heatmap-chart.component.html',
  styleUrl: './heatmap-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatmapChartComponent {
  translate = inject(TranslateService);
  router = inject(Router);
  private readonly echartsConfig = inject(EchartsConfigService);

  dataSource = input<HeatmapDataItem[]>([]);
  gradientRanges = input<GradientRange[]>([]);
  /** Where a click navigates: 'sector' (home) or 'company' (holdings). */
  clickNavigateTo = input<'sector' | 'company'>('sector');
  // Ratio to scale labels' font size. Example: 1 (default), 1.25, 1.5, 2
  fontSizeRatio = input<number>(2);

  chartOptions = computed(() => {
    if (!this.dataSource()?.length) {
      return {};
    }
    return this.echartsConfig.mergeOptions(this.buildChartOptions(this.dataSource()));
  });

  private buildChartOptions(data: HeatmapDataItem[]): EChartsOption {
    const transformedData = data.map((item) => {
      const value = 'volume' in item ? item.volume : 0;
      return {
        name: item.name,
        value,
        changePercentage: item.changePercentage,
        id: item.id,
        itemStyle: {
          color: this.getColorByChangePercentage(item.changePercentage),
        },
        tooltip: {
          formatter: () => {
            return `
            <div>
              <strong>${item.name}</strong><br/>
              ${this.translate.instant('company.market-cap')}: ${this.formatNumber(value)}<br/>
              ${this.translate.instant('company.change-percentage')}: <span dir="ltr">${this.formatChangePercentageForDisplay(item.changePercentage)}</span>
            </div>
          `;
          },
        },
      };
    });

    return {
      tooltip: {
        trigger: 'item',
        show: false,
      },
      series: [
        {
          type: 'treemap',
          data: transformedData,
          width: '100%',
          height: '100%',
          roam: false,
          nodeClick: false,
          breadcrumb: {
            show: false,
          },
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 400,
            overflow: 'truncate',
            formatter: (params: any) => {
              const changePercentage = params.data.changePercentage;
              return `${params.name}\n\n${this.formatChangePercentageForDisplay(changePercentage)}`;
            },
          },
          labelLayout: (params: any) => {
            const { width, height } = params.rect;
            const base = Math.min(width, height);
            const scaled = (base / 8) * this.fontSizeRatio();
            const fontSize = Math.max(10, Math.min(16, scaled)); // min/max with scaling
            return { fontSize };
          },
          itemStyle: {
            borderColor: 'transparent',
            borderWidth: 0.5,
            gapWidth: 3,
          },

          upperLabel: {
            show: false,
            height: 35,
            color: '#fff',
            fontSize: 20,
            fontWeight: 'bold',
            align: 'center',
            verticalAlign: 'middle',
            position: ['50%', '50%'],
          },
        },
      ],
    };
  }

  /** Format percentage so it displays correctly in RTL (avoids "0.59%-" instead of "-0.59%"). */
  private formatChangePercentageForDisplay(value: number | undefined): string {
    if (value == null || Number.isNaN(value)) return '';
    const sign = value >= 0 ? '' : '-';
    const numeric = `${sign}${Math.abs(value).toFixed(2)}%`;
    return `\u200E${numeric}`;
  }

  private formatNumber(value: number): string {
    if (value >= 1e12) {
      return (value / 1e12).toFixed(2) + 'T';
    } else if (value >= 1e9) {
      return (value / 1e9).toFixed(2) + 'B';
    } else if (value >= 1e6) {
      return (value / 1e6).toFixed(2) + 'M';
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(2) + 'K';
    }
    return value.toString();
  }

  private getColorNameToHex(colorName: string): string {
    const colorMap: { [key: string]: string } = {
      'Light Green': '#2F9E4F',
      Green: '#30CC5A',
      'Light Red': '#BF4045',
      Red: '#F63538',
    };
    return colorMap[colorName] || '#414554'; // Default neutral color
  }

  private getColorByChangePercentage(changePercentage: number): string {
    if (changePercentage === 0) {
      return '#414554'; // Neutral for zero change
    }

    const ranges = this.gradientRanges();
    if (!ranges?.length) {
      // Fallback to hardcoded ranges if no backend data
      const fallbackRanges = [
        { min: -Infinity, max: -10, color: '#F63538' },
        { min: -10, max: -5, color: '#BF4045' },
        { min: -5, max: 0, color: '#8B444E' },
        { min: 0, max: 5, color: '#414554' },
        { min: 5, max: 10, color: '#2F9E4F' },
        { min: 10, max: Infinity, color: '#30CC5A' },
      ];
      const range = fallbackRanges.find(
        (r) => changePercentage > r.min && changePercentage <= r.max
      );
      return range ? range.color : '#414554';
    }

    // Use dynamic ranges from backend
    const matchingRange = ranges.find(
      (range) => changePercentage >= range.min && changePercentage <= range.max
    );

    if (matchingRange) {
      return this.getColorNameToHex(matchingRange.color);
    }

    return '#414554'; // Default neutral color if no range matches
  }

  onChartClick(event: unknown) {
    const id = (event as { data?: { id?: string } | null })?.data?.id;
    if (!id) return;
    const target = this.clickNavigateTo();
    if (target === 'company') {
      this.router.navigate(['/sectors/company', id]);
    } else {
      this.router.navigate(['/sectors', id]);
    }
  }
}
